import { Dialog } from "@headlessui/react";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { useEffect, useState } from "react";
import { SystemSetting } from "../../model/SystemSetting";
import { Controller, useForm } from "react-hook-form";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import { SelectFields } from "../../components/select/SelectFields";
import { ConstValue } from "../../config/const";
import companiesQuery from "../../firestore/Companies.query";
import { auth } from "../../firebase";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";

export type ThemeAndButtonModalProp = {
  systemSetting?: SystemSetting;
  onSubmit: (data: SystemSetting) => Promise<void>;
}

export const ThemeAndButtonModal = ({
  open,
  toggle,
  closeOnClickOutside,
  systemSetting,
  onSubmit
}: ModalProp & ThemeAndButtonModalProp) => {

  const dispatch = useDispatch();
  const [optionFlg, setOptionFlg] = useState<{ [key: string]: boolean }>({});
  const { control, handleSubmit, setValue, reset } =
    useForm<SystemSetting>({ defaultValues: systemSetting });
  const selectAppTheme = useInput(systemSetting?.appTheme ?? "なし");
  const selectColorTheme = useInput(systemSetting?.colorTheme ?? "darkColor");
  const selectMypageDispFlg = useInput(systemSetting?.mypageDispFlg ? "true" : "false");
  const selectCodeDispFlg = useInput(systemSetting?.codeDispFlg ? "true" : "false");

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const currentUser = auth.currentUser;
        const optionList = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
        const activeOptions = optionList.filter(isOptionActive);
        const activeOptionMap = activeOptions.reduce((acc, option) => {
          if (option.optionId) acc[option.optionId] = true;
          return acc;
        }, {} as { [key: string]: boolean });

        setOptionFlg(activeOptionMap);
        // オプションが両方無効なとき二次元コードボタンを「非表示」にする
        if (!activeOptionMap["point"] && !activeOptionMap["attendance"]) {
          selectCodeDispFlg.setValue("false");
        }
      } catch (error) {
        console.error("オプション設定の取得に失敗しました", error);
      }
    };

    fetchOptions();
  }, []);

  const colorThemeItem: RadioButtonItem[] = [
    { id: "darkColor", title: "濃い" },
    { id: "lightColor", title: "薄い" },
  ];

  const getDispFlgItem = (targetName: string) => {
    const dispFlgItem: RadioButtonItem[] = [
      { id: targetName + "-true", title: "表示", value: "true" },
      { id: targetName + "-false", title: "非表示", value: "false" },
    ];
    return dispFlgItem;
  }

 // systemSettingが変わったときだけreset
  useEffect(() => {
    reset(systemSetting);
  }, [systemSetting]);

  // 選択値の反映は別useEffectに分ける
  useEffect(() => {
    setValue("colorTheme", selectColorTheme.value);
    setValue("mypageDispFlg", selectMypageDispFlg.value === "true");
    setValue("codeDispFlg", selectCodeDispFlg.value === "true");
  }, [
    selectColorTheme.value,
    selectMypageDispFlg.value,
    selectCodeDispFlg.value,
    setValue,
  ]);

  const onValidSubmit = async (data: SystemSetting) => {
    if (data.codeDispFlg) {
      const pointEnabled = await checkOption("point", dispatch, false);
      const attendanceEnabled = await checkOption("attendance", dispatch, false);
      if (!pointEnabled && !attendanceEnabled) {
        dispatch(
          alertOpen(
            AlertValue(
              "ポイント管理または出欠管理（二次元コード）のオプションが無効のため、二次元コードボタンを表示に設定できません。",
              "danger"
            )
          )
        );
        return;
      }
    }
  
    await onSubmit(data);
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className=" relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-sm ">
          <form onSubmit={handleSubmit(onValidSubmit)}>
            <Dialog.Title
              as="h3"
              className="px-4 pt-4 text-left text-lg font-medium leading-6 text-gray-900"
            >
              {/* テーマとボタン表示設定 //TODO 辰尻：テーマ実装まで画面名を変更 */ }
              ボタン設定
            </Dialog.Title>
            <Dialog.Description></Dialog.Description>
            <div className="overflow-y-auto p-4">
              <div className="h-fit max-h-[calc(100vh_-_16rem)]">
                <FormBox gridcol={"grid-cols-1"} className={"items-center px-5"}>
                  { /* TODO 辰尻：テーマ実装までコメントアウト
                  <Controller
                    name={"appTheme"}
                    control={control}
                    render={({ field, fieldState }) => (
                    <SelectFields
                      {...field}
                      vertical
                      centering={"1/3"}
                      label="テーマ"
                      onChange={(e: any) => {}} // TODO:変更時の処理を追加すること
                      value={selectAppTheme}
                      selectItems={ConstValue.appTheme}
                      className={""}
                      error={fieldState.error}
                      helperText={fieldState.error?.message}
                      style={{'maxWidth': '30%'}}
                    />
                    )}
                  />
                  */
                  }
                  <div>
                    <RadioButton
                      vertical
                      centering={"1/3"}
                      label={'ボタン色（濃度）'}
                      children={undefined}
                      value={selectColorTheme}
                      name={"colorTheme"}
                      radioButtonItem={colorThemeItem}
                      className={""}
                    />
                    <div className={`space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10`}>
                      <span className="basis-1/3"></span>
                      <span className="text-xs text-gray-500 basis-1/2 pt-1 whitespace-pre">※ボタン色が設定されているボタンのみ反映されます</span>
                    </div>
                  </div>
                  <RadioButton
                    vertical
                    centering={"1/3"}
                    label={"マイページボタン表示状態"}
                    children={undefined}
                    value={selectMypageDispFlg}
                    name={"mypageDispFlg"}
                    radioButtonItem={getDispFlgItem("mypageDispFlg")}
                  />
                  {(optionFlg["attendance"] || optionFlg["point"]) && (
                    <RadioButton
                      vertical
                      centering={"1/3"}
                      label={"二次元コードボタン表示状態"}
                      children={undefined}
                      value={selectCodeDispFlg}
                      name={"codeDispFlg"}
                      radioButtonItem={getDispFlgItem("codeDispFlg")}
                    />
                  )}
                </FormBox>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={() => {
                  toggle();
                  reset();
                  let selectCodeDispFlgValue = systemSetting?.codeDispFlg ? "true" : "false";
                  // オプションが両方無効なとき、自動で「非表示」に強制する
                  if (!optionFlg["point"] && !optionFlg["attendance"]) {
                    selectCodeDispFlgValue = "false";
                  }
                  selectColorTheme.setValue(systemSetting?.colorTheme ?? "darkColor");
                  selectMypageDispFlg.setValue(systemSetting?.mypageDispFlg ? "true" : "false");
                  selectCodeDispFlg.setValue(selectCodeDispFlgValue);
                }}
                children={'キャンセル'}
              />
              <Button
                className="mt-1 mr-2 w-[132px]"
                submit
                children={'登録'}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </Modal>
    </>
  )
}